import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';
import { downloadCSV } from '@/lib/csv';
import { todayMexico, addDays, addBillingPeriod, nowMexicoISO, daysBetween } from '@/lib/dates';
import { isIntegerQuantityCategory, roundQuantityForCategory } from '@/lib/productCategory';
import { useRecurringBilling } from '@/hooks/useRecurringBilling';
import { computeNextBillingDate, shouldAdvanceOnPayment } from '@/lib/recurringBilling';
import { filterInvoices } from '@/lib/billingFilters';
import BillingViewToggle from '@/pages/billing/components/BillingViewToggle';
import SendInvoiceEmailModal from '@/pages/billing/components/SendInvoiceEmailModal';
import { buildWhatsAppInvoiceMessage, parseEmailList } from '@/lib/notifications';
import { useLocation } from 'react-router-dom';

interface InvoiceItem {
  type: 'subscription' | 'product' | 'proration';
  ref_id: number;
  name: string;
  sku: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  iva: number;
  total: number;
  billing_type?: string;
  category?: string;
  convert_to_subscription?: boolean;
  convert_cycle?: string;
  subscribed?: boolean;
}

interface Factura {
  id: number;
  folio: string;
  client_id: number;
  issue_date: string;
  due_date: string;
  total_amount: number;
  pending_balance: number;
  status: string;
  sent_at: string | null;
  items: InvoiceItem[];
  client_name?: string;
  payments?: Pago[];
}

interface Pago {
  id: number;
  invoice_id: number;
  amount: number;
  payment_method: string;
  reference: string;
  payment_date: string;
}

interface ClienteOption {
  id: number;
  company_name: string;
  credit_status: string;
  requires_invoice: boolean;
}

interface SuscripcionActiva {
  id: number;
  client_id: number;
  product_id: number;
  quantity: number;
  unit_price: number;
  subtotal: number;
  iva_applied: boolean;
  total_amount: number;
  status: string;
  billing_cycle: string;
  product_name?: string;
  product_sku?: string;
  product_category?: string;
}

interface ProductoCatalogo {
  id: number;
  sku: string;
  name: string;
  unit_price: number;
  category: string;
  billing_type?: string;
}

export default function Cobranza() {
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstatus, setFilterEstatus] = useState('Todos');
  const [viewMode, setViewMode] = useState<'pending' | 'history'>('pending');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(todayMexico().slice(0, 7));
  const [selectedFactura, setSelectedFactura] = useState<Factura | null>(null);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [montoPago, setMontoPago] = useState('');
  const [metodoPago, setMetodoPago] = useState('Transferencia');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [error, setError] = useState('');
  const [clientPhoneMap, setClientPhoneMap] = useState<Map<number, string>>(new Map());
  const [clientEmailMap, setClientEmailMap] = useState<Map<number, string>>(new Map());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState<Factura | null>(null);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);

  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [clientsOpts, setClientsOpts] = useState<ClienteOption[]>([]);
  const location = useLocation();
  const locationState = location.state as { openNewInvoice?: boolean; filterDraft?: boolean } | null;
  const [newInvoice, setNewInvoice] = useState({
    folio: '',
    client_id: '',
    issue_date: todayMexico(),
  });
  const [newInvoiceDueDate, setNewInvoiceDueDate] = useState('');
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [invoiceError, setInvoiceError] = useState('');
  const [invoiceSuccess, setInvoiceSuccess] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceSubtotal, setInvoiceSubtotal] = useState(0);
  const [invoiceIvaTotal, setInvoiceIvaTotal] = useState(0);
  const [selectedClientCredit, setSelectedClientCredit] = useState<string>('none');
  const [selectedClientInvoice, setSelectedClientInvoice] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState<ProductoCatalogo[]>([]);
  const [addProductSearch, setAddProductSearch] = useState('');
  const [addProductQty, setAddProductQty] = useState('1');
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ sku: '', name: '', category: '', unit_price: '' });
  const [newProductError, setNewProductError] = useState('');
  const [creatingProduct, setCreatingProduct] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerFactura, setDrawerFactura] = useState<Factura | null>(null);
  const [drawerPayments, setDrawerPayments] = useState<Pago[]>([]);

  const [editingFolio, setEditingFolio] = useState(false);
  const [newFolio, setNewFolio] = useState('');
  const [folioError, setFolioError] = useState('');
  const [folioSuccess, setFolioSuccess] = useState('');

  const [subOptions, setSubOptions] = useState<SuscripcionActiva[]>([]);
  const [selectedSubIds, setSelectedSubIds] = useState<number[]>([]);

  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Factura | null>(null);
  const [editForm, setEditForm] = useState({ folio: '', issue_date: '', due_date: '', status: 'pending' });
  const [editItems, setEditItems] = useState<InvoiceItem[]>([]);
  const [editAppliesIva, setEditAppliesIva] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [editShowAddProduct, setEditShowAddProduct] = useState(false);
  const [editCatalog, setEditCatalog] = useState<ProductoCatalogo[]>([]);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProductQty, setEditProductQty] = useState('1');
  const [editShowNewProductForm, setEditShowNewProductForm] = useState(false);
  const [editNewProduct, setEditNewProduct] = useState({ sku: '', name: '', category: '', unit_price: '' });
  const [editNewProductError, setEditNewProductError] = useState('');
  const [editCreatingProduct, setEditCreatingProduct] = useState(false);

  const [selectedDraftIds, setSelectedDraftIds] = useState<number[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<{ mode: 'single' | 'bulk'; id?: number } | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  const billingResult = useRecurringBilling();

  useEffect(() => {
    if (locationState?.openNewInvoice) {
      loadClientsForInvoice();
      setShowNewInvoiceModal(true);
      window.history.replaceState({}, document.title);
    }
    if (locationState?.filterDraft) {
      setViewMode('history');
      setFilterEstatus('Borrador');
      window.history.replaceState({}, document.title);
    }
  }, []);

  useEffect(() => {
    fetchFacturas();
  }, []);

  // Auto-cierra el toast de retroalimentación tras unos segundos.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Refresca la lista cuando el motor de fondo genera facturas recurrentes.
  useEffect(() => {
    if (!billingResult) return;
    if (billingResult.created > 0) {
      setToast({
        id: Date.now(),
        message: billingResult.created === 1
          ? 'Se generó 1 factura recurrente automáticamente'
          : `Se generaron ${billingResult.created} facturas recurrentes automáticamente`,
      });
      fetchFacturas();
    }
  }, [billingResult]);

  async function fetchFacturas() {
    try {
      const { data: invs, error: invErr } = await supabase
        .from('erp_invoices')
        .select('*')
        .order('issue_date', { ascending: false });

      if (invErr) throw invErr;
      if (!invs || invs.length === 0) { setFacturas([]); setLoading(false); return; }

      const clientIds = [...new Set(invs.map(i => i.client_id))];
      const invoiceIds = invs.map(i => i.id);

      const [clientsRes, paymentsRes, contactsRes] = await Promise.all([
        supabase.from('erp_clients').select('id, company_name').in('id', clientIds),
        supabase.from('erp_payments').select('*').in('invoice_id', invoiceIds),
        supabase.from('erp_contacts').select('client_id, phone, email, department').in('client_id', clientIds),
      ]);

      const clientMap = new Map((clientsRes.data || []).map(c => [c.id, c.company_name]));

      const paymentsMap = new Map<number, Pago[]>();
      (paymentsRes.data || []).forEach((p: Pago) => {
        if (!paymentsMap.has(p.invoice_id)) paymentsMap.set(p.invoice_id, []);
        paymentsMap.get(p.invoice_id)!.push(p);
      });

      // Prioridad de contacto: el departamento de 'Cobranza' manda; luego
      // 'Finanzas'; cualquier otro contacto sirve como respaldo.
      const priorityOf = (dept: string | null) => (dept === 'Cobranza' ? 3 : dept === 'Finanzas' ? 2 : 1);
      const phoneMap = new Map<number, string>();
      const emailMap = new Map<number, string>();
      const phonePriority = new Map<number, number>();
      const emailPriority = new Map<number, number>();
      (contactsRes.data || []).forEach((c: { client_id: number; phone: string | null; email: string | null; department: string | null }) => {
        if (c.phone) {
          const pr = priorityOf(c.department);
          if (!phoneMap.has(c.client_id) || pr > (phonePriority.get(c.client_id) || 0)) {
            phoneMap.set(c.client_id, c.phone);
            phonePriority.set(c.client_id, pr);
          }
        }
        if (c.email) {
          const pr = priorityOf(c.department);
          if (!emailMap.has(c.client_id) || pr > (emailPriority.get(c.client_id) || 0)) {
            emailMap.set(c.client_id, c.email);
            emailPriority.set(c.client_id, pr);
          }
        }
      });

      setClientPhoneMap(phoneMap);
      setClientEmailMap(emailMap);

      const enriched = invs.map(i => ({
        ...i,
        client_name: clientMap.get(i.client_id) || 'Cliente',
        payments: paymentsMap.get(i.id) || [],
        items: (i.items as InvoiceItem[]) || [],
      }));

      setFacturas(enriched);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadClientsForInvoice() {
    try {
      const { data } = await supabase
        .from('erp_clients')
        .select('id, company_name, credit_status, requires_invoice')
        .order('company_name');
      setClientsOpts(data || []);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  }

  const recalcInvoice = useCallback((items: InvoiceItem[]) => {
    const sub = items.reduce((s, it) => s + it.subtotal, 0);
    const iva = items.reduce((s, it) => s + it.iva, 0);
    const tot = items.reduce((s, it) => s + it.total, 0);
    setInvoiceSubtotal(sub);
    setInvoiceIvaTotal(iva);
    setInvoiceTotal(tot);
  }, []);

  // Calcula el IVA de un concepto. Si el concepto es un prorrateo (type
  // 'proration' o nombre etiquetado con "Prorrateo"), aplica SIEMPRE el 16% al
  // subtotal cuando la factura lleva IVA; de lo contrario conserva la regla
  // previa (si el concepto ya traía IVA, se recalcula al 16%). Así se evita el
  // descuadre contable de líneas de prorrateo sin IVA.
  function computeItemIva(item: InvoiceItem, sub: number, appliesIva: boolean): number {
    const isProration = item.type === 'proration' || /prorrateo/i.test(item.name || '');
    if (isProration) return appliesIva ? sub * 0.16 : 0;
    return Number(item.iva) > 0 ? sub * 0.16 : 0;
  }

  function buildSubscriptionItem(s: SuscripcionActiva): InvoiceItem {
    const qty = Number(s.quantity) || 1;
    const price = Number(s.unit_price) || 0;
    const sub = price * qty;
    const iva = s.iva_applied ? sub * 0.16 : 0;
    return {
      type: 'subscription',
      ref_id: s.id,
      name: s.product_name || 'Producto',
      sku: s.product_sku || '',
      category: s.product_category || '',
      quantity: qty,
      unit_price: price,
      subtotal: sub,
      iva,
      total: sub + iva,
    };
  }

  async function handleClientChangeForInvoice(clientId: string) {
    const client = clientsOpts.find(c => String(c.id) === clientId);
    const dueDate = calcularFechaVencimiento(newInvoice.issue_date, client?.credit_status || 'none');
    setNewInvoice({ ...newInvoice, client_id: clientId });
    setNewInvoiceDueDate(dueDate);
    setSelectedClientCredit(client?.credit_status || 'none');
    setSelectedClientInvoice(client?.requires_invoice || false);

    if (!clientId) {
      setSubOptions([]);
      setSelectedSubIds([]);
      setInvoiceItems([]);
      recalcInvoice([]);
      return;
    }

    try {
      const { data: subs, error: subErr } = await supabase
        .from('erp_subscriptions')
        .select('*')
        .eq('client_id', parseInt(clientId))
        .eq('status', 'active');

      if (subErr) throw subErr;

      if (!subs || subs.length === 0) {
        setSubOptions([]);
        setSelectedSubIds([]);
        setInvoiceItems([]);
        recalcInvoice([]);
        return;
      }

      const productIds = [...new Set(subs.map(s => s.product_id))];
      const { data: prods } = await supabase
        .from('erp_products')
        .select('id, sku, name, category')
        .in('id', productIds);
      const prodMap = new Map((prods || []).map(p => [p.id, p]));

      const enriched: SuscripcionActiva[] = subs.map(s => ({
        ...s,
        product_name: prodMap.get(s.product_id)?.name || 'Producto',
        product_sku: prodMap.get(s.product_id)?.sku || '',
        product_category: prodMap.get(s.product_id)?.category || '',
      }));

      setSubOptions(enriched);
      setSelectedSubIds(enriched.map(s => s.id));

      const subItems = enriched.map(s => buildSubscriptionItem(s));
      setInvoiceItems(subItems);
      recalcInvoice(subItems);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setSubOptions([]);
      setSelectedSubIds([]);
      setInvoiceItems([]);
      recalcInvoice([]);
    }
  }

  function toggleSubscription(id: number) {
    const newIds = selectedSubIds.includes(id)
      ? selectedSubIds.filter(x => x !== id)
      : [...selectedSubIds, id];
    setSelectedSubIds(newIds);
    const subItems = subOptions.filter(s => newIds.includes(s.id)).map(s => buildSubscriptionItem(s));
    const prodItems = invoiceItems.filter(it => it.type === 'product');
    const combined = [...subItems, ...prodItems];
    setInvoiceItems(combined);
    recalcInvoice(combined);
  }

  function selectAllSubs() {
    const ids = subOptions.map(s => s.id);
    setSelectedSubIds(ids);
    const subItems = subOptions.map(s => buildSubscriptionItem(s));
    const prodItems = invoiceItems.filter(it => it.type === 'product');
    const combined = [...subItems, ...prodItems];
    setInvoiceItems(combined);
    recalcInvoice(combined);
  }

  function deselectAllSubs() {
    setSelectedSubIds([]);
    const prodItems = invoiceItems.filter(it => it.type === 'product');
    setInvoiceItems(prodItems);
    recalcInvoice(prodItems);
  }

  function calcularFechaVencimiento(fechaEmision: string, creditStatus: string) {
    if (!fechaEmision) return '';
    return creditStatus === '30days' ? addDays(fechaEmision, 30) : fechaEmision;
  }

  function handleIssueDateChange(date: string) {
    const client = clientsOpts.find(c => String(c.id) === newInvoice.client_id);
    const dueDate = calcularFechaVencimiento(date, client?.credit_status || 'none');
    setNewInvoice({ ...newInvoice, issue_date: date });
    setNewInvoiceDueDate(dueDate);
  }

  function addCatalogProductToInvoice(prod: ProductoCatalogo) {
    const qty = roundQuantityForCategory(parseFloat(addProductQty) || 1, prod.category);
    const price = Number(prod.unit_price);
    const sub = price * qty;
    const iva = selectedClientInvoice ? sub * 0.16 : 0;
    const newItem: InvoiceItem = {
      type: 'product',
      ref_id: prod.id,
      name: prod.name,
      sku: prod.sku,
      billing_type: prod.billing_type,
      category: prod.category,
      quantity: qty,
      unit_price: price,
      subtotal: sub,
      iva,
      total: sub + iva,
    };
    const updated = [...invoiceItems, newItem];
    setInvoiceItems(updated);
    recalcInvoice(updated);
    setShowAddProduct(false);
    setAddProductSearch('');
    setAddProductQty('1');
  }

  function removeInvoiceItem(index: number) {
    const updated = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(updated);
    recalcInvoice(updated);
  }

  function updateInvoiceItem(idx: number, field: 'quantity' | 'unit_price', value: string) {
    const updated = invoiceItems.map((it, i) => {
      if (i !== idx) return it;
      const qty = field === 'quantity' ? roundQuantityForCategory(parseFloat(value) || 1, it.category) : it.quantity;
      const price = field === 'unit_price' ? (parseFloat(value) || 0) : it.unit_price;
      const sub = price * qty;
      const iva = computeItemIva(it, sub, selectedClientInvoice);
      return { ...it, quantity: qty, unit_price: price, subtotal: sub, iva, total: sub + iva };
    });
    setInvoiceItems(updated);
    recalcInvoice(updated);
  }

  function toggleConvertToSubscription(idx: number) {
    const updated = invoiceItems.map((it, i) => {
      if (i !== idx) return it;
      const next = !it.convert_to_subscription;
      return { ...it, convert_to_subscription: next, convert_cycle: next ? (it.convert_cycle || 'monthly') : it.convert_cycle };
    });
    setInvoiceItems(updated);
  }

  function setConvertCycle(idx: number, cycle: string) {
    const updated = invoiceItems.map((it, i) =>
      i !== idx ? it : { ...it, convert_cycle: cycle }
    );
    setInvoiceItems(updated);
  }

  async function handleCreateProductOnFly() {
    setNewProductError('');
    if (!newProduct.sku.trim()) { setNewProductError('El SKU es obligatorio'); return; }
    if (!newProduct.name.trim()) { setNewProductError('El nombre es obligatorio'); return; }
    if (!newProduct.category.trim()) { setNewProductError('La categoría es obligatoria'); return; }
    const price = parseFloat(newProduct.unit_price);
    if (isNaN(price) || price <= 0) { setNewProductError('El precio debe ser mayor a 0'); return; }

    setCreatingProduct(true);
    try {
      const { data, error } = await supabase
        .from('erp_products')
        .insert({
          sku: newProduct.sku.trim().toUpperCase(),
          name: newProduct.name.trim(),
          category: newProduct.category.trim(),
          unit_price: price,
        })
        .select('id, sku, name, unit_price, category, billing_type')
        .single();

      if (error) throw error;

      const qty = roundQuantityForCategory(parseFloat(addProductQty) || 1, data.category);
      const sub = price * qty;
      const iva = selectedClientInvoice ? sub * 0.16 : 0;
      const newItem: InvoiceItem = {
        type: 'product',
        ref_id: data.id,
        name: data.name,
        sku: data.sku,
        billing_type: data.billing_type,
        category: data.category,
        quantity: qty,
        unit_price: price,
        subtotal: sub,
        iva,
        total: sub + iva,
      };
      const updated = [...invoiceItems, newItem];
      setInvoiceItems(updated);
      recalcInvoice(updated);

      setNewProduct({ sku: '', name: '', category: '', unit_price: '' });
      setShowNewProductForm(false);
      setShowAddProduct(false);
      setAddProductSearch('');
      setAddProductQty('1');
    } catch (err: any) {
      setNewProductError(err?.message || 'Error al crear el producto');
    } finally {
      setCreatingProduct(false);
    }
  }

  const filtered = useMemo(() => {
    return filterInvoices(facturas, {
      view: viewMode,
      search,
      status: filterEstatus,
      startDate: filterStartDate,
      endDate: filterEndDate,
      month: selectedMonth,
    });
  }, [facturas, viewMode, search, filterEstatus, filterStartDate, filterEndDate, selectedMonth]);

  // Conteos para el control segmentado (independientes de la búsqueda y filtros manuales).
  const pendingMonthCount = useMemo(
    () => filterInvoices(facturas, { view: 'pending', search: '', status: 'Todos', startDate: '', endDate: '', month: selectedMonth }).length,
    [facturas, selectedMonth],
  );
  const historyPaidCount = useMemo(
    () => filterInvoices(facturas, { view: 'history', search: '', status: 'Todos', startDate: '', endDate: '', month: selectedMonth })
      .filter((f) => f.status === 'paid').length,
    [facturas, selectedMonth],
  );

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  const statusLabel = (s: string) => {
    if (s === 'paid') return 'Pagado';
    if (s === 'overdue') return 'Vencido';
    if (s === 'parcial') return 'Parcial';
    if (s === 'draft') return 'Borrador';
    return 'Pendiente';
  };

  const statusVariant = (s: string) => {
    if (s === 'paid') return 'success';
    if (s === 'overdue') return 'danger';
    if (s === 'parcial') return 'warning';
    if (s === 'draft') return 'neutral';
    return 'warning';
  };

  const totales = useMemo(() => {
    const enMes = (f: Factura) => (f.issue_date || '').slice(0, 7) === selectedMonth;
    const pagoEnMes = (p: Pago) => (p.payment_date || '').slice(0, 7) === selectedMonth;
    const emitido = facturas.filter(enMes).reduce((a, f) => a + Number(f.total_amount), 0);
    const cobrado = facturas.reduce((a, f) => a + (f.payments || []).filter(pagoEnMes).reduce((s, p) => s + Number(p.amount), 0), 0);
    const pendiente = facturas
      .filter(f => f.status !== 'paid' && f.status !== 'draft')
      .reduce((a, f) => a + Number(f.pending_balance), 0);
    const vencido = facturas.filter(f => f.status === 'overdue').reduce((a, f) => a + Number(f.pending_balance), 0);
    return { emitido, pendiente, vencido, cobrado };
  }, [facturas, selectedMonth]);

  // Mensaje de WhatsApp con saludo dinámico según la hora de CDMX (sin mora).
  function getWhatsAppMessage(f: Factura) {
    return buildWhatsAppInvoiceMessage(Number(f.total_amount));
  }

  function handleWhatsApp(f: Factura) {
    const phone = clientPhoneMap.get(f.client_id);
    if (phone) {
      const cleanPhone = phone.replace(/[\s\+\-\(\)]/g, '');
      const msg = getWhatsAppMessage(f);
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      setSelectedFactura(f);
      setPhoneInput('');
      setPhoneError('');
      setShowPhoneModal(true);
    }
  }

  function handleWhatsAppWithPhone() {
    const cleanPhone = phoneInput.replace(/[\s\+\-\(\)]/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      setPhoneError('Ingrese un número de teléfono válido (mínimo 10 dígitos)');
      return;
    }
    if (!selectedFactura) return;
    const msg = getWhatsAppMessage(selectedFactura);
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    setShowPhoneModal(false);
    setSelectedFactura(null);
  }

  // Abre el modal interactivo de envío en lugar de disparar el mailto silencioso.
  function handleEmail(f: Factura) {
    setEmailTarget(f);
    setEmailRecipients(parseEmailList(clientEmailMap.get(f.client_id)));
    setEmailModalOpen(true);
  }

  // Tras un envío exitoso, marca la factura como enviada y avisa al usuario.
  function handleEmailSent() {
    if (!emailTarget) return;
    marcarEnviada(emailTarget);
    setToast({ id: Date.now(), message: `Factura ${emailTarget.folio} enviada por correo` });
  }

  async function marcarEnviada(f: Factura) {
    try {
      const nowStr = nowMexicoISO();
      const { error: updErr } = await supabase
        .from('erp_invoices')
        .update({ sent_at: nowStr })
        .eq('id', f.id);

      if (updErr) throw updErr;
      setFacturas(prev => prev.map(inv => inv.id === f.id ? { ...inv, sent_at: nowStr } : inv));
      if (drawerFactura && drawerFactura.id === f.id) {
        setDrawerFactura({ ...drawerFactura, sent_at: nowStr });
      }
    } catch (err) {
      console.error('Error marking invoice as sent:', err);
    }
  }

  async function desmarcarEnviada(f: Factura) {
    try {
      const { error: updErr } = await supabase
        .from('erp_invoices')
        .update({ sent_at: null })
        .eq('id', f.id);

      if (updErr) throw updErr;
      setFacturas(prev => prev.map(inv => inv.id === f.id ? { ...inv, sent_at: null } : inv));
      if (drawerFactura && drawerFactura.id === f.id) {
        setDrawerFactura({ ...drawerFactura, sent_at: null });
      }
      setToast({ id: Date.now(), message: `Factura ${f.folio} desmarcada como enviada` });
    } catch (err) {
      console.error('Error unmarking invoice as sent:', err);
    }
  }

  function abrirPagoModal(factura: Factura) {
    setSelectedFactura(factura);
    setMontoPago(String(factura.pending_balance));
    setMetodoPago('Transferencia');
    setReferenciaPago('');
    setError('');
    setShowPagoModal(true);
  }

  // Al pagar (total) una factura, avanza la fecha del próximo cobro de cada
  // suscripción contenida en sus conceptos, SOLO cuando la factura corresponde
  // al periodo actual de esa suscripción. Así se evita el doble avance cuando la
  // factura fue auto-generada por el motor de fondo (que ya movió la fecha).
  // Devuelve cuántas suscripciones fueron avanzadas exitosamente.
  async function advanceSubscriptionsOnPaid(items: InvoiceItem[], invoiceIssueDate: string): Promise<number> {
    const subRefIds = (items || [])
      .filter((it) => it.type === 'subscription' && it.ref_id)
      .map((it) => it.ref_id);

    if (subRefIds.length === 0) return 0;

    try {
      const { data: subs } = await supabase
        .from('erp_subscriptions')
        .select('id, client_id, next_billing_date, billing_cycle, status')
        .in('id', subRefIds)
        .eq('status', 'active');

      if (!subs || subs.length === 0) return 0;

      // Facturas del cliente para el candado de idempotencia.
      const clientIds = [...new Set(subs.map((s) => s.client_id))];
      const { data: clientInvoices } = await supabase
        .from('erp_invoices')
        .select('client_id, issue_date, items')
        .in('client_id', clientIds);

      let advanced = 0;
      for (const sub of subs) {
        // Candado 1: solo avanzar si la factura pagada es del periodo actual.
        if (!shouldAdvanceOnPayment(sub, invoiceIssueDate)) continue;

        // Candado 2: no avanzar si el mes destino ya tiene factura.
        const invs = (clientInvoices || []).filter((i) => i.client_id === sub.client_id);
        const nuevaFecha = computeNextBillingDate(sub, invs);
        if (!nuevaFecha) continue;

        const { error } = await supabase
          .from('erp_subscriptions')
          .update({ next_billing_date: nuevaFecha })
          .eq('id', sub.id);
        if (!error) advanced += 1;
      }
      return advanced;
    } catch (err) {
      console.error('Error advancing subscriptions after payment:', err);
      return 0;
    }
  }

  const registrarPago = async () => {
    if (!selectedFactura) return;
    const monto = parseFloat(montoPago);
    if (isNaN(monto) || monto <= 0) {
      setError('Ingrese un monto válido');
      return;
    }
    if (monto > Number(selectedFactura.pending_balance)) {
      setError('El monto no puede superar el balance pendiente');
      return;
    }

    try {
      const { error: payErr } = await supabase
        .from('erp_payments')
        .insert({
          invoice_id: selectedFactura.id,
          amount: monto,
          payment_method: metodoPago,
          reference: referenciaPago || null,
          payment_date: todayMexico(),
        });

      if (payErr) throw payErr;

      const nuevoBalance = Number(selectedFactura.pending_balance) - monto;
      let nuevoStatus: string;
      if (nuevoBalance <= 0) {
        nuevoStatus = 'paid';
      } else {
        nuevoStatus = 'parcial';
      }

      const { error: updErr } = await supabase
        .from('erp_invoices')
        .update({ pending_balance: nuevoBalance, status: nuevoStatus })
        .eq('id', selectedFactura.id);

      if (updErr) throw updErr;

      // Si el pago cubre el total, avanzar las suscripciones al siguiente ciclo.
      let advancedCount = 0;
      if (nuevoStatus === 'paid') {
        advancedCount = await advanceSubscriptionsOnPaid(selectedFactura.items, selectedFactura.issue_date);
      }

      await fetchFacturas();
      setShowPagoModal(false);
      setSelectedFactura(null);

      // Retroalimentación visual del avance de suscripciones.
      setToast({
        id: Date.now(),
        message: advancedCount > 0
          ? `¡Pago registrado con éxito! ${advancedCount === 1 ? 'Se avanzó 1 suscripción' : `Se avanzaron ${advancedCount} suscripciones`} al siguiente periodo de cobro`
          : '¡Pago registrado con éxito!',
      });
    } catch (err) {
      console.error('Error registering payment:', err);
      setError('Error al registrar el pago. Intente de nuevo.');
    }
  };

  async function openDrawer(factura: Factura) {
    setDrawerFactura(factura);

    try {
      const { data: pagos } = await supabase
        .from('erp_payments')
        .select('*')
        .eq('invoice_id', factura.id)
        .order('payment_date', { ascending: false });
      setDrawerPayments(pagos || []);
    } catch {
      setDrawerPayments([]);
    }

    let clientData = null;
    try {
      const { data: cli } = await supabase
        .from('erp_clients')
        .select('id, company_name, rfc, address, credit_status, requires_invoice')
        .eq('id', factura.client_id)
        .maybeSingle();
      clientData = cli;
    } catch { /* noop */ }

    setDrawerFactura({ ...factura, client_name: clientData?.company_name || factura.client_name });
    setDrawerOpen(true);
  }

  function handleExportCSV() {
    if (filtered.length === 0) return;
    const headers = ['Folio', 'Cliente', 'Emisión', 'Vencimiento', 'Monto Total', 'Balance Pendiente', 'Conceptos', 'Estatus', 'Días Vencido'];
    const rows = filtered.map(f => {
      const diasVencido = f.status === 'overdue'
        ? daysBetween(f.due_date, todayMexico())
        : 0;
      const conceptos = (f.items || []).map(it => `${it.name} x${it.quantity}`).join('; ');
      return [
        f.folio,
        f.client_name || '',
        f.issue_date,
        f.due_date,
        String(f.total_amount),
        String(f.pending_balance),
        conceptos,
        statusLabel(f.status),
        String(diasVencido),
      ];
    });
    downloadCSV(headers, rows, `facturacion-${todayMexico()}.csv`);
  }

  function resetNewInvoiceForm() {
    setNewInvoice({
      folio: '',
      client_id: '',
      issue_date: todayMexico(),
    });
    setNewInvoiceDueDate('');
    setInvoiceItems([]);
    setInvoiceTotal(0);
    setInvoiceSubtotal(0);
    setInvoiceIvaTotal(0);
    setSelectedClientCredit('none');
    setSelectedClientInvoice(false);
    setInvoiceError('');
    setInvoiceSuccess('');
    setShowAddProduct(false);
    setAddProductSearch('');
    setAddProductQty('1');
    setSubOptions([]);
    setSelectedSubIds([]);
    setShowNewProductForm(false);
    setNewProduct({ sku: '', name: '', category: '', unit_price: '' });
    setNewProductError('');
    setCreatingProduct(false);
  }

  async function handleCreateInvoice() {
    setInvoiceError('');
    setInvoiceSuccess('');

    if (!newInvoice.folio.trim()) { setInvoiceError('El folio es obligatorio'); return; }
    if (!newInvoice.client_id) { setInvoiceError('Seleccione un cliente'); return; }
    if (!newInvoice.issue_date) { setInvoiceError('La fecha de emisión es obligatoria'); return; }
    if (invoiceItems.length === 0) { setInvoiceError('Agregue al menos un concepto a la factura'); return; }
    if (invoiceTotal <= 0) { setInvoiceError('El total debe ser mayor a 0'); return; }

    const client = clientsOpts.find(c => String(c.id) === newInvoice.client_id);
    const dueDate = calcularFechaVencimiento(newInvoice.issue_date, client?.credit_status || 'none');

    const today = todayMexico();
    let invoiceStatus = 'pending';
    if (dueDate < today) {
      invoiceStatus = 'overdue';
    }

    try {
      const { data: newInv, error } = await supabase
        .from('erp_invoices')
        .insert({
          folio: newInvoice.folio.trim().toUpperCase(),
          client_id: parseInt(newInvoice.client_id),
          issue_date: newInvoice.issue_date,
          due_date: dueDate,
          total_amount: invoiceTotal,
          pending_balance: invoiceTotal,
          status: invoiceStatus,
          items: invoiceItems,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Convertir a suscripción: crear registros activos para los conceptos marcados.
      const convertItems = invoiceItems.filter((it) => it.convert_to_subscription && it.type === 'product' && it.ref_id);
      let createdSubs = 0;
      for (const it of convertItems) {
        const cycle = it.convert_cycle || 'monthly';
        const nextBilling = addBillingPeriod(newInvoice.issue_date, cycle);
        const { error: subErr } = await supabase.from('erp_subscriptions').insert({
          client_id: parseInt(newInvoice.client_id),
          product_id: it.ref_id,
          start_date: newInvoice.issue_date,
          billing_cycle: cycle,
          next_billing_date: nextBilling,
          unit_price: it.unit_price,
          quantity: it.quantity,
          subtotal: it.subtotal,
          iva_applied: Number(it.iva) > 0,
          total_amount: it.total,
          status: 'active',
        });
        if (subErr) throw subErr;
        createdSubs += 1;
      }

      // Marcar como ya suscritos los conceptos convertidos para no duplicar la
      // suscripción si la factura se edita posteriormente.
      if (createdSubs > 0 && newInv) {
        const subscribedItems = invoiceItems.map((it) =>
          it.convert_to_subscription ? { ...it, subscribed: true } : it
        );
        await supabase.from('erp_invoices').update({ items: subscribedItems }).eq('id', newInv.id);
      }

      setInvoiceSuccess(createdSubs > 0
        ? `Factura creada exitosamente · ${createdSubs === 1 ? '1 suscripción generada' : `${createdSubs} suscripciones generadas`}`
        : 'Factura creada exitosamente');
      await fetchFacturas();

      setTimeout(() => {
        setShowNewInvoiceModal(false);
        resetNewInvoiceForm();
      }, 800);

    } catch (err: any) {
      setInvoiceError(err?.message || 'Error al crear la factura. Intente de nuevo.');
    }
  }

  async function handleUpdateFolio() {
    setFolioError('');
    setFolioSuccess('');

    if (!drawerFactura || !newFolio.trim()) {
      setFolioError('El folio no puede estar vacío');
      return;
    }

    try {
      const { error } = await supabase
        .from('erp_invoices')
        .update({ folio: newFolio.trim().toUpperCase() })
        .eq('id', drawerFactura.id);

      if (error) {
        if (error.message?.includes('duplicate') || error.message?.includes('unique') || error.code === '23505') {
          setFolioError('Este folio ya existe. Use un folio único.');
        } else {
          setFolioError(error.message || 'Error al actualizar el folio');
        }
        return;
      }

      setFolioSuccess('Folio actualizado correctamente');
      setDrawerFactura({ ...drawerFactura, folio: newFolio.trim().toUpperCase() });
      setEditingFolio(false);
      await fetchFacturas();

      setTimeout(() => setFolioSuccess(''), 2000);
    } catch (err: any) {
      setFolioError(err?.message || 'Error al actualizar el folio');
    }
  }

  async function openEditInvoice(factura: Factura) {
    setEditingInvoice(factura);
    setEditForm({
      folio: factura.folio,
      issue_date: factura.issue_date,
      due_date: factura.due_date,
      status: factura.status,
    });
    setEditItems((factura.items || []).map(it => ({ ...it })));
    setEditError('');
    setEditSuccess('');
    setEditShowAddProduct(false);
    setEditProductSearch('');
    setEditProductQty('1');
    setEditShowNewProductForm(false);
    setEditNewProduct({ sku: '', name: '', category: '', unit_price: '' });
    setEditNewProductError('');
    setEditCreatingProduct(false);

    let appliesIva = (factura.items || []).some(it => Number(it.iva) > 0);
    if ((factura.items || []).length === 0) {
      try {
        const { data: cli } = await supabase
          .from('erp_clients')
          .select('requires_invoice')
          .eq('id', factura.client_id)
          .maybeSingle();
        appliesIva = cli?.requires_invoice ?? false;
      } catch { /* noop */ }
    }
    setEditAppliesIva(appliesIva);
    setShowEditInvoiceModal(true);
  }

  function updateEditItem(idx: number, field: 'quantity' | 'unit_price', value: string) {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const qty = field === 'quantity' ? roundQuantityForCategory(parseFloat(value) || 1, it.category) : it.quantity;
      const price = field === 'unit_price' ? (parseFloat(value) || 0) : it.unit_price;
      const sub = price * qty;
      const iva = computeItemIva(it, sub, editAppliesIva);
      return { ...it, quantity: qty, unit_price: price, subtotal: sub, iva, total: sub + iva };
    }));
  }

  function removeEditItem(idx: number) {
    setEditItems(prev => prev.filter((_, i) => i !== idx));
  }

  function toggleEditConvert(idx: number) {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = !it.convert_to_subscription;
      return { ...it, convert_to_subscription: next, convert_cycle: next ? (it.convert_cycle || 'monthly') : it.convert_cycle };
    }));
  }

  function setEditConvertCycle(idx: number, cycle: string) {
    setEditItems(prev => prev.map((it, i) => i !== idx ? it : { ...it, convert_cycle: cycle }));
  }

  function addProductToEdit(prod: ProductoCatalogo) {
    const qty = roundQuantityForCategory(parseFloat(editProductQty) || 1, prod.category);
    const price = Number(prod.unit_price);
    const sub = price * qty;
    const iva = editAppliesIva ? sub * 0.16 : 0;
    const newItem: InvoiceItem = {
      type: 'product',
      ref_id: prod.id,
      name: prod.name,
      sku: prod.sku,
      billing_type: prod.billing_type,
      category: prod.category,
      quantity: qty,
      unit_price: price,
      subtotal: sub,
      iva,
      total: sub + iva,
    };
    setEditItems(prev => [...prev, newItem]);
    setEditShowAddProduct(false);
    setEditProductSearch('');
    setEditProductQty('1');
  }

  async function openEditAddProduct() {
    const { data } = await supabase.from('erp_products').select('id, sku, name, unit_price, category, billing_type').order('category').order('name');
    setEditCatalog(data || []);
    setEditProductSearch('');
    setEditProductQty('1');
    setEditShowNewProductForm(false);
    setEditShowAddProduct(true);
  }

  async function handleEditCreateProductOnFly() {
    setEditNewProductError('');
    if (!editNewProduct.sku.trim()) { setEditNewProductError('El SKU es obligatorio'); return; }
    if (!editNewProduct.name.trim()) { setEditNewProductError('El nombre es obligatorio'); return; }
    if (!editNewProduct.category.trim()) { setEditNewProductError('La categoría es obligatoria'); return; }
    const price = parseFloat(editNewProduct.unit_price);
    if (isNaN(price) || price <= 0) { setEditNewProductError('El precio debe ser mayor a 0'); return; }

    setEditCreatingProduct(true);
    try {
      const { data, error } = await supabase
        .from('erp_products')
        .insert({
          sku: editNewProduct.sku.trim().toUpperCase(),
          name: editNewProduct.name.trim(),
          category: editNewProduct.category.trim(),
          unit_price: price,
        })
        .select('id, sku, name, unit_price, category, billing_type')
        .single();

      if (error) throw error;

      const qty = roundQuantityForCategory(parseFloat(editProductQty) || 1, data.category);
      const sub = price * qty;
      const iva = editAppliesIva ? sub * 0.16 : 0;
      const newItem: InvoiceItem = {
        type: 'product',
        ref_id: data.id,
        name: data.name,
        sku: data.sku,
        billing_type: data.billing_type,
        category: data.category,
        quantity: qty,
        unit_price: price,
        subtotal: sub,
        iva,
        total: sub + iva,
      };
      setEditItems(prev => [...prev, newItem]);

      setEditNewProduct({ sku: '', name: '', category: '', unit_price: '' });
      setEditShowNewProductForm(false);
      setEditShowAddProduct(false);
      setEditProductSearch('');
      setEditProductQty('1');
    } catch (err: any) {
      setEditNewProductError(err?.message || 'Error al crear el producto');
    } finally {
      setEditCreatingProduct(false);
    }
  }

  async function handleSaveInvoiceEdit() {
    setEditError('');
    setEditSuccess('');

    if (!editingInvoice) return;
    if (!editForm.folio.trim()) { setEditError('El folio es obligatorio'); return; }
    if (!editForm.issue_date) { setEditError('La fecha de emisión es obligatoria'); return; }
    if (!editForm.due_date) { setEditError('La fecha de vencimiento es obligatoria'); return; }
    if (editItems.length === 0) { setEditError('La factura debe tener al menos un concepto'); return; }
    if (editTotal <= 0) { setEditError('El total debe ser mayor a 0'); return; }

    try {
      const { data: pays } = await supabase
        .from('erp_payments')
        .select('amount')
        .eq('invoice_id', editingInvoice.id);
      const paidSum = (pays || []).reduce((s, p) => s + Number(p.amount), 0);
      const nuevoPendiente = Math.max(0, editTotal - paidSum);

      let nuevoStatus = editForm.status;
      if (nuevoPendiente <= 0 && nuevoStatus !== 'draft') {
        nuevoStatus = 'paid';
      }

      const { error } = await supabase
        .from('erp_invoices')
        .update({
          folio: editForm.folio.trim().toUpperCase(),
          issue_date: editForm.issue_date,
          due_date: editForm.due_date,
          total_amount: editTotal,
          pending_balance: nuevoPendiente,
          status: nuevoStatus,
          items: editItems,
        })
        .eq('id', editingInvoice.id);

      if (error) {
        if (error.message?.includes('duplicate') || error.message?.includes('unique') || error.code === '23505') {
          setEditError('Este folio ya existe. Use un folio único.');
        } else {
          setEditError(error.message || 'Error al actualizar la factura');
        }
        return;
      }

      // Convertir a suscripción: crear registros activos para conceptos marcados
      // que aún no han sido suscritos previamente (paridad con Nueva Factura).
      const editConvertItems = editItems.filter((it) => it.convert_to_subscription && it.type === 'product' && it.ref_id && !it.subscribed);
      let editCreatedSubs = 0;
      for (const it of editConvertItems) {
        // Deduplicación: verificar si ya existe una suscripción activa para este
        // mismo cliente y producto vinculada a la operación (misma fecha de
        // emisión), evitando duplicados al re-editar una factura ya procesada.
        const { data: existingSub } = await supabase
          .from('erp_subscriptions')
          .select('id')
          .eq('client_id', editingInvoice.client_id)
          .eq('product_id', it.ref_id)
          .eq('status', 'active')
          .eq('start_date', editForm.issue_date)
          .maybeSingle();

        if (existingSub) {
          continue;
        }

        const cycle = it.convert_cycle || 'monthly';
        const nextBilling = addBillingPeriod(editForm.issue_date, cycle);
        const { error: subErr } = await supabase.from('erp_subscriptions').insert({
          client_id: editingInvoice.client_id,
          product_id: it.ref_id,
          start_date: editForm.issue_date,
          billing_cycle: cycle,
          next_billing_date: nextBilling,
          unit_price: it.unit_price,
          quantity: it.quantity,
          subtotal: it.subtotal,
          iva_applied: Number(it.iva) > 0,
          total_amount: it.total,
          status: 'active',
        });
        if (subErr) throw subErr;
        editCreatedSubs += 1;
      }

      // Marcar como ya suscritos para no duplicar en una futura edición.
      if (editCreatedSubs > 0) {
        const subscribedEditItems = editItems.map((it) =>
          it.convert_to_subscription ? { ...it, subscribed: true } : it
        );
        await supabase.from('erp_invoices').update({ items: subscribedEditItems }).eq('id', editingInvoice.id);
        setEditItems(subscribedEditItems);
      }

      setEditSuccess(editCreatedSubs > 0
        ? `Factura actualizada correctamente · ${editCreatedSubs === 1 ? '1 suscripción generada' : `${editCreatedSubs} suscripciones generadas`}`
        : 'Factura actualizada correctamente');

      // Si se marca como pagada (y no lo estaba), avanzar suscripciones al siguiente ciclo.
      let advancedCount = 0;
      if (editingInvoice.status !== 'paid' && nuevoStatus === 'paid') {
        advancedCount = await advanceSubscriptionsOnPaid(editItems, editForm.issue_date);
      }

      // Retroalimentación visual del avance de suscripciones.
      setToast({
        id: Date.now(),
        message: advancedCount > 0
          ? `¡Factura marcada como pagada! ${advancedCount === 1 ? 'Se avanzó 1 suscripción' : `Se avanzaron ${advancedCount} suscripciones`} al siguiente periodo de cobro`
          : '¡Factura marcada como pagada!',
      });

      await fetchFacturas();

      if (drawerFactura && drawerFactura.id === editingInvoice.id) {
        setDrawerFactura({
          ...drawerFactura,
          folio: editForm.folio.trim().toUpperCase(),
          issue_date: editForm.issue_date,
          due_date: editForm.due_date,
          total_amount: editTotal,
          pending_balance: nuevoPendiente,
          status: nuevoStatus,
          items: editItems,
        });
      }

      setTimeout(() => {
        setShowEditInvoiceModal(false);
        setEditingInvoice(null);
        setEditSuccess('');
      }, 800);
    } catch (err: any) {
      setEditError(err?.message || 'Error al actualizar la factura');
    }
  }

  const editSubtotal = editItems.reduce((s, it) => s + (Number(it.subtotal) || 0), 0);
  const editIvaTotal = editItems.reduce((s, it) => s + (Number(it.iva) || 0), 0);
  const editTotal = editItems.reduce((s, it) => s + (Number(it.total) || 0), 0);
  const editFilteredCatalog = editCatalog.filter(p =>
    p.name.toLowerCase().includes(editProductSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(editProductSearch.toLowerCase())
  );

  const filteredCatalog = catalogProducts.filter(p =>
    p.name.toLowerCase().includes(addProductSearch.toLowerCase()) ||
    p.sku.toLowerCase().includes(addProductSearch.toLowerCase())
  );
  const existingCategories = [...new Set(catalogProducts.map(p => p.category).filter(Boolean))];

  function toggleSelectDraft(id: number) {
    setSelectedDraftIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleSelectAllDrafts() {
    const draftIds = filtered.filter(f => f.status === 'draft').map(f => f.id);
    const allSelected = draftIds.length > 0 && draftIds.every(id => selectedDraftIds.includes(id));
    setSelectedDraftIds(allSelected ? [] : draftIds);
  }

  function requestDeleteDraft(f: Factura) {
    setBulkError('');
    setConfirmDelete({ mode: 'single', id: f.id });
  }

  async function confirmDeleteInvoice() {
    if (!confirmDelete) return;
    setBulkError('');
    setBulkProcessing(true);
    try {
      if (confirmDelete.mode === 'single' && confirmDelete.id) {
        const { error } = await supabase.from('erp_invoices').delete().eq('id', confirmDelete.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('erp_invoices').delete().in('id', selectedDraftIds);
        if (error) throw error;
        setSelectedDraftIds([]);
      }
      if (drawerFactura && confirmDelete.id === drawerFactura.id) setDrawerOpen(false);
      setConfirmDelete(null);
      await fetchFacturas();
    } catch (err: any) {
      setBulkError(err?.message || 'Error al eliminar el borrador');
    } finally {
      setBulkProcessing(false);
    }
  }

  async function handleBulkApprove() {
    setBulkError('');
    setBulkProcessing(true);
    try {
      const { error } = await supabase
        .from('erp_invoices')
        .update({ status: 'pending' })
        .in('id', selectedDraftIds);
      if (error) throw error;
      setSelectedDraftIds([]);
      await fetchFacturas();
    } catch (err: any) {
      setBulkError(err?.message || 'Error al emitir las facturas');
    } finally {
      setBulkProcessing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando facturación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Facturación y Cobranza</h1>
          <p className="text-sm text-foreground-500 mt-0.5">{facturas.length} facturas en el sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-download-2-line"></i>
            Exportar CSV
          </button>
          <button
            onClick={() => { resetNewInvoiceForm(); loadClientsForInvoice(); setShowNewInvoiceModal(true); }}
            className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Nueva Factura
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-file-list-3-line text-foreground-500 text-sm"></i>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Total Emitido (Mes)</span>
          </div>
          <p className="text-xl font-bold text-foreground-900">{formatCurrency(totales.emitido)}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-time-line text-amber-500 text-sm"></i>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Pendiente Cobro (Acumulado)</span>
          </div>
          <p className="text-xl font-bold text-amber-600">{formatCurrency(totales.pendiente)}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-alert-line text-red-500 text-sm"></i>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Cartera Vencida (Acumulado)</span>
          </div>
          <p className="text-xl font-bold text-red-600">{formatCurrency(totales.vencido)}</p>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <i className="ri-check-double-line text-emerald-500 text-sm"></i>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Cobrado (Mes)</span>
          </div>
          <p className="text-xl font-bold text-emerald-600">{formatCurrency(totales.cobrado)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <BillingViewToggle
          view={viewMode}
          onChange={setViewMode}
          pendingCount={pendingMonthCount}
          historyCount={historyPaidCount}
        />
        <p className="text-xs text-foreground-500 flex items-center gap-1.5">
          <i className="ri-information-line text-foreground-400"></i>
          {viewMode === 'pending'
            ? 'Cuentas por cobrar del mes y saldos pendientes de meses anteriores'
            : 'Historial liquidado (pagadas) del mes y meses anteriores'}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por Folio o Cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
          />
        </div>
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
          title="Fecha desde"
        />
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
          title="Fecha hasta"
        />
        <select
          value={filterEstatus}
          onChange={(e) => setFilterEstatus(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
        >
          <option value="Todos">Todos los estados</option>
          <option value="Borrador">Borrador</option>
          <option value="Pagado">Pagado</option>
          <option value="Pendiente">Pendiente</option>
          <option value="Parcial">Parcial</option>
          <option value="Vencido">Vencido</option>
        </select>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
          title="Mes para KPIs (Emitido / Cobrado)"
        />
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-background-100 bg-background-50">
                <th className="text-center px-4 py-3 w-10 font-semibold text-foreground-500">
                  <input
                    type="checkbox"
                    checked={filtered.filter(f => f.status === 'draft').length > 0 && filtered.filter(f => f.status === 'draft').every(f => selectedDraftIds.includes(f.id))}
                    onChange={toggleSelectAllDrafts}
                    className="w-4 h-4 rounded accent-primary-600 cursor-pointer"
                    title="Seleccionar todos los borradores"
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Folio</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Conceptos</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Emisión</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-500">Vencimiento</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-500">Monto</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-500">Pendiente</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Estatus</th>
                <th className="text-center px-4 py-3 font-semibold text-foreground-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {filtered.map((f) => {
                const diasVencido = f.status === 'overdue'
                  ? daysBetween(f.due_date, todayMexico())
                  : 0;
                const totalPagado = (f.payments || []).reduce((s, p) => s + Number(p.amount), 0);

                return (
                  <tr key={f.id} className="hover:bg-background-50 transition-colors cursor-pointer" onClick={() => openDrawer(f)}>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {f.status === 'draft' && (
                        <input
                          type="checkbox"
                          checked={selectedDraftIds.includes(f.id)}
                          onChange={() => toggleSelectDraft(f.id)}
                          className="w-4 h-4 rounded accent-primary-600 cursor-pointer"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-primary-600">{f.folio}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-foreground-800">{f.client_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(f.items || []).slice(0, 3).map((it, idx) => (
                          <span key={idx} className="text-[10px] bg-background-100 px-1.5 py-0.5 rounded text-foreground-600 font-medium">
                            {it.name} <span className="text-foreground-400">x{it.quantity}</span>
                          </span>
                        ))}
                        {(f.items || []).length > 3 && (
                          <span className="text-[10px] text-foreground-400 font-medium">+{f.items.length - 3} más</span>
                        )}
                        {(f.items || []).length === 0 && <span className="text-[10px] text-foreground-400">Sin conceptos</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-600">
                      {new Date(f.issue_date + 'T00:00:00').toLocaleDateString('es-MX')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-foreground-600">
                          {new Date(f.due_date + 'T00:00:00').toLocaleDateString('es-MX')}
                        </span>
                        {f.status === 'overdue' && (
                          <span className="text-[10px] font-bold text-red-600">{diasVencido} días vencido</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-800">{formatCurrency(Number(f.total_amount))}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${Number(f.pending_balance) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(Number(f.pending_balance))}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <Badge variant={statusVariant(f.status)}>{statusLabel(f.status)}</Badge>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-0.5">
                        {f.status === 'draft' && (
                          <button
                            onClick={() => requestDeleteDraft(f)}
                            className="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-colors"
                            title="Eliminar borrador"
                          >
                            <i className="ri-delete-bin-line text-red-500 text-xs"></i>
                          </button>
                        )}
                        {f.sent_at ? (
                          <button
                            onClick={() => desmarcarEnviada(f)}
                            className="w-7 h-7 rounded-md bg-sky-100 hover:bg-sky-200 flex items-center justify-center cursor-pointer transition-colors"
                            title={`Factura enviada el ${new Date(f.sent_at).toLocaleDateString('es-MX')}. Clic para desmarcar.`}
                          >
                            <i className="ri-checkbox-circle-fill text-sky-600 text-xs"></i>
                          </button>
                        ) : (
                          f.status !== 'draft' && (
                            <button
                              onClick={() => marcarEnviada(f)}
                              className="w-7 h-7 rounded-md bg-sky-50 hover:bg-sky-100 flex items-center justify-center cursor-pointer transition-colors"
                              title="Marcar Factura como Enviada manualmente"
                            >
                              <i className="ri-send-plane-line text-sky-500 text-xs"></i>
                            </button>
                          )
                        )}
                        {Number(f.pending_balance) > 0 && f.status !== 'draft' && (
                          <button
                            onClick={() => abrirPagoModal(f)}
                            className="w-7 h-7 rounded-md bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center cursor-pointer transition-colors"
                            title="Registrar Pago"
                          >
                            <i className="ri-money-dollar-circle-line text-emerald-500 text-xs"></i>
                          </button>
                        )}
                        {f.status !== 'draft' && (
                          <button
                            onClick={() => handleWhatsApp(f)}
                            className="w-7 h-7 rounded-md bg-green-50 hover:bg-green-100 flex items-center justify-center cursor-pointer transition-colors"
                            title="Enviar WhatsApp"
                          >
                            <i className="ri-whatsapp-line text-green-600 text-xs"></i>
                          </button>
                        )}
                        {f.status !== 'draft' && (
                          <button
                            onClick={() => handleEmail(f)}
                            className="w-7 h-7 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                            title="Enviar Correo"
                          >
                            <i className="ri-mail-line text-foreground-500 text-xs"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-12 text-center">
            <i className="ri-bill-line text-foreground-300 text-3xl"></i>
            <p className="text-sm text-foreground-500 mt-2">No se encontraron facturas</p>
          </div>
        )}
      </div>

      <Modal open={showPagoModal} onClose={() => setShowPagoModal(false)} title="Registrar Pago">
        {selectedFactura && (
          <div className="space-y-4">
            <div className="bg-background-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-foreground-500">Folio</span>
                <span className="text-xs font-semibold text-foreground-800">{selectedFactura.folio}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-foreground-500">Cliente</span>
                <span className="text-xs font-semibold text-foreground-800">{selectedFactura.client_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-foreground-500">Monto Total</span>
                <span className="text-xs font-semibold text-foreground-800">{formatCurrency(Number(selectedFactura.total_amount))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-foreground-500">Balance Pendiente</span>
                <span className="text-xs font-bold text-red-600">{formatCurrency(Number(selectedFactura.pending_balance))}</span>
              </div>
              {Number(selectedFactura.pending_balance) < Number(selectedFactura.total_amount) && (
                <div className="flex justify-between">
                  <span className="text-xs text-foreground-500">Pagado hasta ahora</span>
                  <span className="text-xs font-semibold text-emerald-600">{formatCurrency(Number(selectedFactura.total_amount) - Number(selectedFactura.pending_balance))}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Monto del Pago</label>
              <input
                type="number"
                value={montoPago}
                onChange={(e) => { setMontoPago(e.target.value); setError(''); }}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                max={Number(selectedFactura.pending_balance)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Método de Pago</label>
              <select
                value={metodoPago}
                onChange={(e) => setMetodoPago(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="Transferencia">Transferencia Bancaria</option>
                <option value="Depósito">Depósito</option>
                <option value="Cheque">Cheque</option>
                <option value="Efectivo">Efectivo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Referencia</label>
              <input
                type="text"
                value={referenciaPago}
                onChange={(e) => setReferenciaPago(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Número de referencia o comprobante"
              />
            </div>
            {error && (
              <p className="text-xs text-red-600 font-medium">{error}</p>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700">
                <i className="ri-information-line mr-1"></i>
                Si el monto es menor al balance pendiente, la factura quedará en estado <strong>Parcial</strong> con un badge naranja.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowPagoModal(false)}
                className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={registrarPago}
                className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 cursor-pointer transition-colors whitespace-nowrap"
              >
                Registrar Pago
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showPhoneModal} onClose={() => { setShowPhoneModal(false); setSelectedFactura(null); }} title="Ingresar Teléfono">
        {selectedFactura && (
          <div className="space-y-4">
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-700">
                <strong>{selectedFactura.client_name}</strong> no tiene un número de teléfono registrado. Ingrese el número para enviar el recordatorio por WhatsApp.
              </p>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <p className="text-xs text-foreground-600 mb-2 font-medium">Mensaje a enviar:</p>
              <p className="text-[11px] text-foreground-500 italic leading-relaxed">{getWhatsAppMessage(selectedFactura)}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Número de WhatsApp</label>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setPhoneError(''); }}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej: +52 1 55 1234 5678"
              />
              {phoneError && (
                <p className="text-xs text-red-600 mt-1">{phoneError}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowPhoneModal(false); setSelectedFactura(null); }}
                className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleWhatsAppWithPhone}
                className="px-4 py-2 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-whatsapp-line"></i>
                Enviar WhatsApp
              </button>
            </div>
          </div>
        )}
      </Modal>

      <SendInvoiceEmailModal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setEmailTarget(null); }}
        invoice={emailTarget}
        initialRecipients={emailRecipients}
        onSent={handleEmailSent}
      />

      <Modal open={showNewInvoiceModal} onClose={() => { setShowNewInvoiceModal(false); resetNewInvoiceForm(); }} title="Nueva Factura" size="xl">
        <div className="space-y-4">
          {invoiceSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{invoiceSuccess}</p>
            </div>
          )}
          {invoiceError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{invoiceError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Folio <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newInvoice.folio}
                onChange={(e) => setNewInvoice({ ...newInvoice, folio: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                placeholder="Ej. FAC-2026-001"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Cliente <span className="text-red-500">*</span></label>
              <select
                value={newInvoice.client_id}
                onChange={(e) => handleClientChangeForInvoice(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="">Seleccionar cliente...</option>
                {clientsOpts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name} {c.credit_status === '30days' ? '(Crédito 30 Días)' : '(Sin Crédito)'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Emisión <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={newInvoice.issue_date}
                onChange={(e) => handleIssueDateChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Vencimiento</label>
              <div className="w-full px-3 py-2 text-sm border border-background-100 rounded-lg bg-background-50 text-foreground-700">
                {newInvoiceDueDate
                  ? new Date(newInvoiceDueDate + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                  : 'Seleccione cliente y fecha de emisión'}
              </div>
              {newInvoiceDueDate && (
                <p className="text-[10px] text-foreground-500 mt-1">
                  {selectedClientCredit === '30days' ? 'Calculado: emisión + 30 días (crédito)' : 'Calculado: pago de contado (mismo día)'}
                </p>
              )}
            </div>
          </div>

          {subOptions.length > 0 && (
            <div className="border-t border-background-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900">Suscripciones del Cliente</h4>
                  <p className="text-[10px] text-foreground-500 mt-0.5">Desmarca las que NO quieras incluir en esta factura</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={selectAllSubs}
                    className="px-2 py-1 text-[10px] font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    Todas
                  </button>
                  <button
                    onClick={deselectAllSubs}
                    className="px-2 py-1 text-[10px] font-medium text-foreground-500 bg-background-100 rounded-md hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    Ninguna
                  </button>
                </div>
              </div>
              <div className="border border-background-200 rounded-lg divide-y divide-background-100 max-h-48 overflow-y-auto">
                {subOptions.map(s => {
                  const qty = Number(s.quantity) || 1;
                  const price = Number(s.unit_price) || 0;
                  const iva = s.iva_applied ? price * qty * 0.16 : 0;
                  const tot = price * qty + iva;
                  const checked = selectedSubIds.includes(s.id);
                  const cycleShort = s.billing_cycle === 'monthly' ? 'Mensual' : s.billing_cycle === 'one_time' ? 'Único' : s.billing_cycle === 'quarterly' ? 'Trimestral' : s.billing_cycle === 'semi_annual' ? 'Semestral' : 'Anual';
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-primary-50/40' : 'bg-white opacity-60'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSubscription(s.id)}
                        className="w-4 h-4 rounded border-background-300 accent-primary-600 cursor-pointer flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-foreground-800 block truncate">{s.product_name}</span>
                        <span className="text-[10px] text-foreground-500">{s.product_sku} · {qty} uds · {cycleShort}</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap">{formatCurrency(tot)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-background-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground-900">Conceptos de la Factura</h4>
              <button
                onClick={async () => {
                  const { data } = await supabase.from('erp_products').select('id, sku, name, unit_price, category, billing_type').order('category').order('name');
                  setCatalogProducts(data || []);
                  setShowAddProduct(true);
                  setAddProductSearch('');
                  setAddProductQty('1');
                }}
                disabled={!newInvoice.client_id}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line text-xs"></i>
                Agregar Producto Adicional
              </button>
            </div>

            {newInvoice.client_id && invoiceItems.length === 0 && subOptions.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-amber-700">
                  <i className="ri-information-line mr-1"></i>
                  Este cliente no tiene suscripciones activas. Puede agregar productos del catálogo manualmente con el botón "Agregar Producto Adicional".
                </p>
              </div>
            )}

            {invoiceItems.length > 0 && (
              <div className="border border-background-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      <th className="text-left px-3 py-2 font-semibold text-foreground-500">Tipo</th>
                      <th className="text-left px-3 py-2 font-semibold text-foreground-500">Concepto</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground-500">Cant.</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">P. Unitario</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">Subtotal</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">IVA</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">Total</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground-500"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {invoiceItems.map((it, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <Badge variant={it.type === 'subscription' ? 'info' : it.type === 'proration' ? 'warning' : (it.convert_to_subscription ? 'info' : 'neutral')}>
                            {it.type === 'subscription' ? 'Suscripción' : it.type === 'proration' ? 'Prorrateo' : (it.convert_to_subscription ? '→ Suscripción' : 'Producto')}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-medium text-foreground-800">{it.name}</span>
                          {it.sku && <span className="text-[10px] text-foreground-500 block">{it.sku}</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            value={it.quantity}
                            onChange={(e) => updateInvoiceItem(idx, 'quantity', e.target.value)}
                            className="w-14 px-1.5 py-1 text-xs text-center border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                            min="1"
                            step={isIntegerQuantityCategory(it.category) ? '1' : 'any'}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={it.unit_price}
                            onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)}
                            className="w-20 px-1.5 py-1 text-xs text-right border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                            min="0"
                            step="any"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-foreground-700">{formatCurrency(it.subtotal)}</td>
                        <td className="px-3 py-2 text-right">
                          {it.iva > 0 ? (
                            <span className="text-primary-600 font-medium">{formatCurrency(it.iva)}</span>
                          ) : (
                            <span className="text-foreground-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-foreground-800">{formatCurrency(it.total)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {it.type === 'product' && it.billing_type === 'recurring' && (
                              <>
                                <button
                                  onClick={() => toggleConvertToSubscription(idx)}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${it.convert_to_subscription ? 'bg-accent-500 text-white hover:bg-accent-600' : 'bg-accent-50 text-accent-700 hover:bg-accent-100'}`}
                                  title={it.convert_to_subscription ? 'Se creará una suscripción activa al guardar la factura' : 'Convertir este concepto en una suscripción recurrente'}
                                >
                                  <i className="ri-repeat-line text-xs"></i>
                                  {it.convert_to_subscription ? 'Suscripción' : 'Convertir a Suscripción'}
                                </button>
                                {it.convert_to_subscription && (
                                  <select
                                    value={it.convert_cycle || 'monthly'}
                                    onChange={(e) => setConvertCycle(idx, e.target.value)}
                                    className="w-24 px-1.5 py-1 text-[10px] border border-accent-200 rounded-md bg-white focus:outline-none focus:border-accent-400 text-foreground-700 cursor-pointer"
                                    title="Ciclo de facturación de la suscripción"
                                  >
                                    <option value="monthly">Mensual</option>
                                    <option value="quarterly">Trimestral</option>
                                    <option value="semi_annual">Semestral</option>
                                    <option value="annual">Anual</option>
                                  </select>
                                )}
                              </>
                            )}
                            {it.type === 'product' && (
                              <button
                                onClick={() => removeInvoiceItem(idx)}
                                className="w-6 h-6 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-colors"
                                title="Quitar concepto"
                              >
                                <i className="ri-close-line text-red-500 text-[10px]"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {invoiceItems.length > 0 && (
              <div className="mt-3 flex justify-end">
                <div className="bg-background-50 rounded-lg p-4 w-64 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">Subtotal</span>
                    <span className="font-semibold text-foreground-800">{formatCurrency(invoiceSubtotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">IVA</span>
                    <span className={`font-semibold ${invoiceIvaTotal > 0 ? 'text-primary-600' : 'text-foreground-400'}`}>
                      {formatCurrency(invoiceIvaTotal)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-background-200 pt-2">
                    <span className="font-semibold text-foreground-800">Total</span>
                    <span className="font-bold text-foreground-900">{formatCurrency(invoiceTotal)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showAddProduct && (
            <div className="border border-background-200 rounded-lg p-4 bg-background-50 space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-semibold text-foreground-700">Buscar Producto del Catálogo</h5>
                <button onClick={() => setShowAddProduct(false)} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={addProductSearch}
                  onChange={(e) => setAddProductSearch(e.target.value)}
                  placeholder="Buscar por nombre o SKU..."
                  className="flex-1 px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                />
                <input
                  type="number"
                  value={addProductQty}
                  onChange={(e) => setAddProductQty(e.target.value)}
                  className="w-20 px-2 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                  placeholder="Cant."
                  min="1"
                  step="any"
                />
              </div>
              <div className="max-h-36 overflow-y-auto space-y-1">
                {filteredCatalog.slice(0, 8).map(prod => (
                  <div
                    key={prod.id}
                    onClick={() => addCatalogProductToInvoice(prod)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-white cursor-pointer transition-colors border border-transparent hover:border-background-200"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-mono text-foreground-500 bg-white px-1 py-0.5 rounded">{prod.sku}</span>
                      <span className="text-xs text-foreground-800 truncate">{prod.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap ml-2">{formatCurrency(Number(prod.unit_price))}</span>
                  </div>
                ))}
                {filteredCatalog.length === 0 && (
                  <p className="text-xs text-foreground-400 py-3 text-center">No se encontraron productos</p>
                )}
              </div>

              <div className="border-t border-background-200 pt-3">
                {!showNewProductForm ? (
                  <button
                    onClick={() => { setNewProductError(''); setShowNewProductForm(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-box-line text-xs"></i>
                    Registrar nuevo producto al catálogo
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-semibold text-foreground-700">Nuevo Producto</h5>
                      <button onClick={() => { setShowNewProductForm(false); setNewProductError(''); }} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                    {newProductError && (
                      <p className="text-xs text-red-600 font-medium">{newProductError}</p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">SKU <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={newProduct.sku}
                          onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value })}
                          placeholder="Ej. LIC-WIALON"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Precio base (MXN) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          value={newProduct.unit_price}
                          onChange={(e) => setNewProduct({ ...newProduct, unit_price: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="any"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Nombre <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          placeholder="Ej. Licencia Wialon Hosting"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Categoría <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={newProduct.category}
                          onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                          placeholder="Ej. Licencias"
                          list="category-options"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                        <datalist id="category-options">
                          {existingCategories.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setShowNewProductForm(false); setNewProductError(''); }}
                        className="px-3 py-1.5 text-[11px] font-medium text-foreground-600 bg-background-100 rounded-md hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleCreateProductOnFly}
                        disabled={creatingProduct}
                        className="px-3 py-1.5 text-[11px] font-semibold text-white bg-accent-600 rounded-md hover:bg-accent-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                      >
                        <i className="ri-save-line"></i>
                        {creatingProduct ? 'Guardando...' : 'Guardar y Agregar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowNewInvoiceModal(false); resetNewInvoiceForm(); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateInvoice}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Guardar Factura
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showEditInvoiceModal} onClose={() => { setShowEditInvoiceModal(false); setEditingInvoice(null); }} title="Editar Factura" size="xl">
        <div className="space-y-4">
          {editSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{editSuccess}</p>
            </div>
          )}
          {editError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{editError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Folio <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={editForm.folio}
                onChange={(e) => setEditForm({ ...editForm, folio: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Estatus</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="draft">Borrador</option>
                <option value="pending">Pendiente</option>
                <option value="paid">Pagado</option>
                <option value="parcial">Parcial</option>
                <option value="overdue">Vencido</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Emisión <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={editForm.issue_date}
                onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Fecha de Vencimiento <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={editForm.due_date}
                onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              />
            </div>
          </div>

          <div className="border-t border-background-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-foreground-900">Conceptos de la Factura</h4>
              <button
                onClick={openEditAddProduct}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-xs"></i>
                Agregar Producto
              </button>
            </div>

            {editItems.length > 0 ? (
              <div className="border border-background-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-background-50 border-b border-background-200">
                      <th className="text-left px-3 py-2 font-semibold text-foreground-500">Tipo</th>
                      <th className="text-left px-3 py-2 font-semibold text-foreground-500">Concepto</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground-500">Cant.</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">P. Unitario</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">Subtotal</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">IVA</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground-500">Total</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground-500"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {editItems.map((it, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <Badge variant={it.type === 'subscription' ? 'info' : it.type === 'proration' ? 'warning' : (it.convert_to_subscription ? 'info' : 'neutral')}>
                            {it.type === 'subscription' ? 'Suscripción' : it.type === 'proration' ? 'Prorrateo' : (it.convert_to_subscription ? '→ Suscripción' : 'Producto')}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-medium text-foreground-800">{it.name}</span>
                          {it.sku && <span className="text-[10px] text-foreground-500 block">{it.sku}</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            value={it.quantity}
                            onChange={(e) => updateEditItem(idx, 'quantity', e.target.value)}
                            className="w-14 px-1.5 py-1 text-xs text-center border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                            min="1"
                            step={isIntegerQuantityCategory(it.category) ? '1' : 'any'}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            value={it.unit_price}
                            onChange={(e) => updateEditItem(idx, 'unit_price', e.target.value)}
                            className="w-20 px-1.5 py-1 text-xs text-right border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                            min="0"
                            step="any"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-foreground-700">{formatCurrency(it.subtotal)}</td>
                        <td className="px-3 py-2 text-right">
                          {Number(it.iva) > 0 ? <span className="text-primary-600">{formatCurrency(it.iva)}</span> : <span className="text-foreground-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-foreground-800">{formatCurrency(it.total)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {it.type === 'product' && it.billing_type === 'recurring' && (
                              <>
                                <button
                                  onClick={() => toggleEditConvert(idx)}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${it.convert_to_subscription ? 'bg-accent-500 text-white hover:bg-accent-600' : 'bg-accent-50 text-accent-700 hover:bg-accent-100'}`}
                                  title={it.convert_to_subscription ? 'Se creará una suscripción activa al guardar la factura' : 'Convertir este concepto en una suscripción recurrente'}
                                >
                                  <i className="ri-repeat-line text-xs"></i>
                                  {it.convert_to_subscription ? 'Suscripción' : 'Convertir a Suscripción'}
                                </button>
                                {it.convert_to_subscription && (
                                  <select
                                    value={it.convert_cycle || 'monthly'}
                                    onChange={(e) => setEditConvertCycle(idx, e.target.value)}
                                    className="w-24 px-1.5 py-1 text-[10px] border border-accent-200 rounded-md bg-white focus:outline-none focus:border-accent-400 text-foreground-700 cursor-pointer"
                                    title="Ciclo de facturación de la suscripción"
                                  >
                                    <option value="monthly">Mensual</option>
                                    <option value="quarterly">Trimestral</option>
                                    <option value="semi_annual">Semestral</option>
                                    <option value="annual">Anual</option>
                                  </select>
                                )}
                              </>
                            )}
                            <button
                              onClick={() => removeEditItem(idx)}
                              className="w-6 h-6 rounded-md bg-red-50 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-colors"
                              title="Quitar concepto"
                            >
                              <i className="ri-close-line text-red-500 text-[10px]"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700">Esta factura no tiene conceptos. Agrega productos o servicios con el botón "Agregar Producto".</p>
              </div>
            )}

            {editItems.length > 0 && (
              <div className="mt-3 flex justify-end">
                <div className="bg-background-50 rounded-lg p-4 w-64 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">Subtotal</span>
                    <span className="font-semibold text-foreground-800">{formatCurrency(editSubtotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-500">IVA</span>
                    <span className={`font-semibold ${editIvaTotal > 0 ? 'text-primary-600' : 'text-foreground-400'}`}>{formatCurrency(editIvaTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-background-200 pt-2">
                    <span className="font-semibold text-foreground-800">Total</span>
                    <span className="font-bold text-foreground-900">{formatCurrency(editTotal)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {editShowAddProduct && (
            <div className="border border-background-200 rounded-lg p-4 bg-background-50 space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-semibold text-foreground-700">Buscar Producto del Catálogo</h5>
                <button onClick={() => setEditShowAddProduct(false)} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editProductSearch}
                  onChange={(e) => setEditProductSearch(e.target.value)}
                  placeholder="Buscar por nombre o SKU..."
                  className="flex-1 px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                />
                <input
                  type="number"
                  value={editProductQty}
                  onChange={(e) => setEditProductQty(e.target.value)}
                  className="w-20 px-2 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-800"
                  placeholder="Cant."
                  min="1"
                  step="any"
                />
              </div>
              <div className="max-h-36 overflow-y-auto space-y-1">
                {editFilteredCatalog.slice(0, 8).map(prod => (
                  <div
                    key={prod.id}
                    onClick={() => addProductToEdit(prod)}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-white cursor-pointer transition-colors border border-transparent hover:border-background-200"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-mono text-foreground-500 bg-white px-1 py-0.5 rounded">{prod.sku}</span>
                      <span className="text-xs text-foreground-800 truncate">{prod.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap ml-2">{formatCurrency(Number(prod.unit_price))}</span>
                  </div>
                ))}
                {editFilteredCatalog.length === 0 && (
                  <p className="text-xs text-foreground-400 py-3 text-center">No se encontraron productos</p>
                )}
              </div>

              <div className="border-t border-background-200 pt-3">
                {!editShowNewProductForm ? (
                  <button
                    onClick={() => { setEditNewProductError(''); setEditShowNewProductForm(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-box-line text-xs"></i>
                    Registrar nuevo producto al catálogo
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-xs font-semibold text-foreground-700">Nuevo Producto</h5>
                      <button onClick={() => { setEditShowNewProductForm(false); setEditNewProductError(''); }} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                    {editNewProductError && (
                      <p className="text-xs text-red-600 font-medium">{editNewProductError}</p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">SKU <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={editNewProduct.sku}
                          onChange={(e) => setEditNewProduct({ ...editNewProduct, sku: e.target.value })}
                          placeholder="Ej. LIC-WIALON"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Precio base (MXN) <span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          value={editNewProduct.unit_price}
                          onChange={(e) => setEditNewProduct({ ...editNewProduct, unit_price: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="any"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Nombre <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={editNewProduct.name}
                          onChange={(e) => setEditNewProduct({ ...editNewProduct, name: e.target.value })}
                          placeholder="Ej. Licencia Wialon Hosting"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-semibold text-foreground-600 mb-1">Categoría <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={editNewProduct.category}
                          onChange={(e) => setEditNewProduct({ ...editNewProduct, category: e.target.value })}
                          placeholder="Ej. Licencias"
                          list="edit-category-options"
                          className="w-full px-2.5 py-1.5 text-xs border border-background-200 rounded-md focus:outline-none focus:border-primary-400 text-foreground-800"
                        />
                        <datalist id="edit-category-options">
                          {existingCategories.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setEditShowNewProductForm(false); setEditNewProductError(''); }}
                        className="px-3 py-1.5 text-[11px] font-medium text-foreground-600 bg-background-100 rounded-md hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleEditCreateProductOnFly}
                        disabled={editCreatingProduct}
                        className="px-3 py-1.5 text-[11px] font-semibold text-white bg-accent-600 rounded-md hover:bg-accent-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
                      >
                        <i className="ri-save-line"></i>
                        {editCreatingProduct ? 'Guardando...' : 'Guardar y Agregar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowEditInvoiceModal(false); setEditingInvoice(null); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveInvoiceEdit}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Guardar Cambios
            </button>
          </div>
        </div>
      </Modal>

      {drawerOpen && drawerFactura && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)}></div>
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
              <div>
                {editingFolio ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newFolio}
                      onChange={(e) => { setNewFolio(e.target.value.toUpperCase()); setFolioError(''); }}
                      className="w-48 px-2 py-1 text-sm font-mono border border-primary-300 rounded-md focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                      placeholder="FAC-2026-001"
                      autoFocus
                    />
                    <button
                      onClick={handleUpdateFolio}
                      className="w-7 h-7 rounded-md bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center cursor-pointer transition-colors"
                      title="Guardar folio"
                    >
                      <i className="ri-check-line text-emerald-600 text-xs"></i>
                    </button>
                    <button
                      onClick={() => { setEditingFolio(false); setFolioError(''); }}
                      className="w-7 h-7 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                      title="Cancelar"
                    >
                      <i className="ri-close-line text-foreground-500 text-xs"></i>
                    </button>
                    {folioError && <span className="text-[10px] text-red-600 font-medium">{folioError}</span>}
                    {folioSuccess && <span className="text-[10px] text-emerald-600 font-medium">{folioSuccess}</span>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground-900">{drawerFactura.folio}</h3>
                    <button
                      onClick={() => { setNewFolio(drawerFactura.folio); setEditingFolio(true); setFolioError(''); setFolioSuccess(''); }}
                      className="w-6 h-6 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                      title="Editar folio"
                    >
                      <i className="ri-edit-line text-foreground-500 text-[10px]"></i>
                    </button>
                  </div>
                )}
                <p className="text-xs text-foreground-500">{drawerFactura.client_name}</p>
              </div>
              <div className="flex items-center gap-2">
                {drawerFactura.status !== 'draft' && (
                  <button
                    onClick={() => handleEmail(drawerFactura)}
                    className="px-3 py-1.5 text-xs font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
                    title="Enviar factura por correo"
                  >
                    <i className="ri-mail-line text-primary-600"></i>
                    Enviar Correo
                  </button>
                )}
                {drawerFactura.sent_at && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200" title={`Marcada como enviada el ${drawerFactura.sent_at}`}>
                    <i className="ri-checkbox-circle-fill text-sky-500 text-xs"></i>
                    Enviada
                    <button
                      onClick={() => desmarcarEnviada(drawerFactura)}
                      className="ml-1 text-sky-400 hover:text-red-500 cursor-pointer"
                      title="Desmarcar como enviada"
                    >
                      <i className="ri-close-circle-fill text-[11px]"></i>
                    </button>
                  </span>
                )}
                {(drawerFactura.status === 'draft' || drawerFactura.status === 'pending') && (
                  <button
                    onClick={() => openEditInvoice(drawerFactura)}
                    className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-edit-line"></i>
                    Editar
                  </button>
                )}
                <Badge variant={statusVariant(drawerFactura.status)}>{statusLabel(drawerFactura.status)}</Badge>
                {(() => {
                  const generatedSubs = (drawerFactura.items || []).filter((it) => it.convert_to_subscription).length;
                  if (generatedSubs === 0) return null;
                  return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 whitespace-nowrap" title="Esta factura generó suscripciones automáticas">
                      <i className="ri-repeat-line"></i>
                      {generatedSubs === 1 ? '1 suscripción generada' : `${generatedSubs} suscripciones generadas`}
                    </span>
                  );
                })()}
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-foreground-600"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <div className="bg-background-50 rounded-xl p-4 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Emisión</span>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">
                    {new Date(drawerFactura.issue_date + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Vencimiento</span>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">
                    {new Date(drawerFactura.due_date + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Monto Total</span>
                  <p className="text-lg font-bold text-foreground-900 mt-0.5">{formatCurrency(Number(drawerFactura.total_amount))}</p>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Pendiente</span>
                  <p className={`text-lg font-bold mt-0.5 ${Number(drawerFactura.pending_balance) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(Number(drawerFactura.pending_balance))}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-list-check-2 text-foreground-500"></i>
                  Conceptos Cobrados
                </h4>
                {(drawerFactura.items || []).length > 0 ? (
                  <div className="border border-background-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-background-50 border-b border-background-200">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-500">Concepto</th>
                          <th className="text-center px-3 py-2 font-semibold text-foreground-500">Cant.</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-500">Subtotal</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-500">IVA</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-500">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-100">
                        {(drawerFactura.items || []).map((it, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-foreground-800">{it.name}</span>
                                <Badge variant={it.type === 'subscription' ? 'info' : it.type === 'proration' ? 'warning' : 'neutral'}>
                                  {it.type === 'subscription' ? 'Sub' : it.type === 'proration' ? 'Prorr.' : 'Prod'}
                                </Badge>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center text-foreground-700">{it.quantity}</td>
                            <td className="px-3 py-2 text-right text-foreground-700">{formatCurrency(it.subtotal)}</td>
                            <td className="px-3 py-2 text-right">
                              {it.iva > 0 ? <span className="text-primary-600">{formatCurrency(it.iva)}</span> : <span className="text-foreground-400">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-foreground-800">{formatCurrency(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 py-4 text-center bg-background-50 rounded-lg">No se registraron conceptos para esta factura</p>
                )}
              </div>

              {(() => {
                const prorationItems = (drawerFactura.items || []).filter(
                  (it) => it.type === 'proration' || /prorrateo/i.test(it.name || ''),
                );
                if (prorationItems.length === 0) return null;
                return (
                  <div>
                    <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-1.5">
                      <i className="ri-timer-line text-amber-500"></i>
                      Desglose de Prorrateo
                    </h4>
                    <div className="space-y-2">
                      {prorationItems.map((it, idx) => (
                        <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0">
                              <i className="ri-stack-line text-amber-600 mt-0.5 text-sm"></i>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-amber-800 leading-snug">{it.name}</p>
                                <p className="text-[10px] text-amber-700 mt-0.5">
                                  {it.quantity} unidad(es) · Periodo prorrateado al corte
                                </p>
                              </div>
                            </div>
                            <span className="text-sm font-bold text-amber-800 whitespace-nowrap">{formatCurrency(Number(it.total))}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pt-2 border-t border-amber-200 text-[10px] text-amber-700">
                            <span>Subtotal: <strong>{formatCurrency(Number(it.subtotal))}</strong></span>
                            <span>IVA: <strong>{formatCurrency(Number(it.iva))}</strong></span>
                            <span>Unitario: <strong>{formatCurrency(Number(it.unit_price))}</strong></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div>
                <h4 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-money-dollar-circle-line text-foreground-500"></i>
                  Historial de Pagos ({drawerPayments.length})
                </h4>
                {drawerPayments.length > 0 ? (
                  <div className="border border-background-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-background-50 border-b border-background-200">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-500">Fecha</th>
                          <th className="text-left px-3 py-2 font-semibold text-foreground-500">Método</th>
                          <th className="text-left px-3 py-2 font-semibold text-foreground-500">Referencia</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-500">Monto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-100">
                        {drawerPayments.map((p) => (
                          <tr key={p.id}>
                            <td className="px-3 py-2 text-foreground-600">
                              {new Date(p.payment_date + 'T00:00:00').toLocaleDateString('es-MX')}
                            </td>
                            <td className="px-3 py-2 text-foreground-600">{p.payment_method}</td>
                            <td className="px-3 py-2 text-foreground-600 font-mono text-[10px]">{p.reference || '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-600">{formatCurrency(Number(p.amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 py-6 text-center bg-background-50 rounded-lg">Sin pagos registrados para esta factura</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {selectedDraftIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-foreground-900 text-white rounded-full pl-5 pr-2 py-2 flex items-center gap-3 shadow-xl max-w-full overflow-x-auto">
          <span className="text-xs font-medium whitespace-nowrap">
            {selectedDraftIds.length} borrador{selectedDraftIds.length === 1 ? '' : 'es'} seleccionado{selectedDraftIds.length === 1 ? '' : 's'}
          </span>
          <button
            onClick={handleBulkApprove}
            disabled={bulkProcessing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-check-double-line"></i>
            Emitir / Pasar a Pendientes
          </button>
          <button
            onClick={() => { setBulkError(''); setConfirmDelete({ mode: 'bulk' }); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-full hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Eliminar
          </button>
          <button
            onClick={() => setSelectedDraftIds([])}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0"
            title="Cancelar selección"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      )}

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Confirmar Eliminación" size="sm">
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-700">
              {confirmDelete?.mode === 'single'
                ? 'Se eliminará este borrador de forma permanente. Esta acción no se puede deshacer.'
                : `Se eliminarán ${selectedDraftIds.length} borrador(es) de forma permanente. Esta acción no se puede deshacer.`}
            </p>
          </div>
          {bulkError && (
            <p className="text-xs text-red-600 font-medium">{bulkError}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { setConfirmDelete(null); setBulkError(''); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={confirmDeleteInvoice}
              disabled={bulkProcessing}
              className="px-4 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
            >
              <i className="ri-delete-bin-line"></i>
              {bulkProcessing ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>

      {toast && (
        <div className="fixed top-6 right-6 z-[60] animate-slide-in-right">
          <div className="flex items-start gap-3 bg-white border border-background-200 rounded-xl px-4 py-3 max-w-sm">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-emerald-600 text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground-900">Éxito</p>
              <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(null)}
              className="text-foreground-400 hover:text-foreground-600 cursor-pointer flex-shrink-0"
              title="Cerrar"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}